import { FC, useState, ReactElement, useEffect } from "react";
import { Box, Typography, TextField } from "@mui/material";
import { css } from "@emotion/css";

const MAX_LENGTH = 250;
const CHARACTERS_PER_ROW = 70;

const styles = {
  noteCreateContainer: css`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
  `,
  noteCreateFooter: css`
    display: flex;
    justify-content: space-between;
  `,
};

const sxStyles = {
  noteCreateTextField: {
    "& .MuiInputBase-input": {
      fontSize: "0.875rem",
      fontWeight: "400",
      fontFamily: "Roboto, sans-serif, Helvetica, Arial",
    },
  },
  wordCount: {
    color: "text.secondary",
    fontSize: "0.75rem",
  },
};

interface NoteCreateProps {
  placeholder: string;
  noteText?: string;
  button?: ReactElement;
  onSubmit?: (text: string) => void;
}

const NoteCreate: FC<NoteCreateProps> = ({
  placeholder,
  noteText,
  button,
  onSubmit,
}) => {
  const [text, setText] = useState<string>(noteText || "");
  const [rows, setRows] = useState<number>(1);

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newText = event.target.value;
    if (newText.length <= MAX_LENGTH) {
      setText(newText);
    }
  };

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit(text);
    }
    setText("");
  };

  useEffect(() => {
    if (text.length > 0) {
      setRows(Math.ceil(text.length / CHARACTERS_PER_ROW));
    }
  }, [text]);

  return (
    <Box className={styles.noteCreateContainer}>
      <TextField
        placeholder={placeholder}
        size="small"
        multiline
        rows={rows}
        variant="standard"
        value={text}
        onChange={handleTextChange}
        fullWidth
        sx={{
          ...sxStyles.noteCreateTextField,
        }}
      />
      <Box className={styles.noteCreateFooter}>
        <Typography variant="caption" sx={sxStyles.wordCount}>
          {text.length}/{MAX_LENGTH} characters
        </Typography>

        {button && <Box onClick={handleSubmit}>{button}</Box>}
      </Box>
    </Box>
  );
};

export default NoteCreate;
